import {
  type CurrencyIso,
  ProductLegalNotice,
  StockIndication
} from '@spa/generated/graphql/graphql';
import { AttributeValue } from '@spa/components/product-specifications/AttributeTypes';
import { CapacityUnitCode } from '@spa/lib/converters/convertCapacityUnitCode';
import { ProductAssetsFields } from '@spa/components/ProductAssetTypes';
import { ProductBadge } from './ProductBadge';
import { StateCartPrice } from '@spa/lib/store/carts/CartsState';

export { ProductLegalNotice } from '@spa/generated/graphql/graphql';
export { ProductBadge } from './ProductBadge';

export interface HomeDeliveryDeliveryMode {
  readonly description: string | null;
  readonly freeAbove: {
    readonly valueWithTax: number;
    readonly currencyIso: CurrencyIso;
  } | null;
}

export interface ProductInfoProductSibling extends ProductAssetsFields {
  readonly buyable: boolean;
  readonly badges: ReadonlyArray<ProductBadge> | null;
  readonly capacityUnitCode: CapacityUnitCode;
  readonly capacityValue: string;
  readonly clothingSize: string | null;
  readonly code: string;
  readonly inkColor: string | null;
  readonly innerPackCount: AttributeValue | null;
  readonly manufacturerColor: string | null;
  readonly manufacturerColorNumber: string | null;
  readonly path: string;
  readonly sizeRange: string | null;
  readonly stockLevel: number;
  readonly storageCapacity: string | null;
  readonly storageUnit: string | null;
}

export interface ProductInfoProductImage {
  readonly digitalSourceType?: string | null;
  readonly height: number;
  readonly url: string;
  readonly width: number;
}

interface ProductInfoProductCategories {
  readonly name: string;
}

export interface ProductInfoProductBrand {
  readonly image: ProductInfoProductImage | null;
  readonly name: string;
  readonly path: string;
  readonly normalizedSlug: string;
  readonly routable: boolean;
}

export interface BasePrice {
  readonly value: number;
  readonly capacity: {
    readonly unitCode: CapacityUnitCode;
    readonly value: string;
  };
}

export interface ProductPreOrderSettings {
  readonly validFrom: string;
  readonly validTo: string;
  readonly unlimited: boolean;
  readonly isActive: boolean;
}

export interface ProductAgeRestrictionSettings {
  readonly ageRestrictionAge: number;
  readonly ageRestrictionType: string;
}

export interface ProductInfoProduct extends ProductAssetsFields {
  readonly ageRestrictionSettings: ProductAgeRestrictionSettings | null;
  readonly buyable: boolean;
  readonly badges: ReadonlyArray<ProductBadge> | null;
  readonly averageRating: number | null;
  readonly reviewCount: number | null;
  readonly basePrice: BasePrice | null;
  readonly brand: ProductInfoProductBrand | null;
  readonly campaignPrice: StateCartPrice | null;
  readonly capacityUnitCode: CapacityUnitCode;
  readonly capacityValue: string;
  readonly categories: ReadonlyArray<ProductInfoProductCategories>;
  readonly categoryWithParents: ReadonlyArray<ProductInfoProductCategories>;
  readonly clickAndCollect: boolean | null;
  readonly clothingSize: string | null;
  readonly code: string;
  readonly currentPrice: StateCartPrice;
  readonly ean: string | null;
  readonly hasFreeSamples: boolean | null;
  readonly homeDelivery: boolean | null;
  readonly images: ReadonlyArray<ProductInfoProductImage> | null;
  readonly innerPackCount: AttributeValue | null;
  readonly inkColor: string | null;
  readonly manufacturerArticleNumber: string | null;
  readonly manufacturerColor: string | null;
  readonly manufacturerColorNumber: string | null;
  readonly name: string;
  readonly ownBrand: boolean | null;
  readonly path: string;
  readonly preOrderSettings: ProductPreOrderSettings | null;
  readonly recommendedRetailPrice: StateCartPrice | null;
  readonly releaseDate: string | null;
  readonly requiredLegalNotices: ReadonlyArray<ProductLegalNotice> | null;
  readonly saleMaximumQuantity: number | null;
  readonly saleMinimumQuantity: number | null;
  readonly saleQuantityIncrements: number | null;
  readonly shortDescriptionHtml: string | null;
  readonly descriptionHtml: string | null;
  readonly siblings: ReadonlyArray<ProductInfoProductSibling>;
  readonly sizeRange: string | null;
  readonly stockLevel: number;
  readonly stockLevelIndication: StockIndication;
  readonly storageCapacity: string | null;
  readonly storageUnit: string | null;
  readonly strikePrice: StateCartPrice | null;
  readonly subBrand: ProductInfoProductBrand | null;
}
